import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
  Icon,
} from '@mui/material';
import LocationOn from '@mui/icons-material/LocationOn';

import moment from 'moment';
import { formatDateString } from '../../../utils/dateHelper';
import TaskActions from '../../TaskActions';
import Horse from '../../Horse';
import User from '../../User';
import * as brandColors from '../../../utils/brandColors';
import TaskStateLabel from '../../TaskStateLabel';
import {
  setGlobalModals,
  setSelectedBarnHorse,
} from '../../../redux/app/actions';
import {
  STATUS_CHANGE_MODAL_TYPE,
  TASK_MODAL_TYPE,
} from '../../../redux/app/types';

export const getTaskName = (task, workflow, schedule) => {
  if (task && task.id) {
    return task.name
  }
  let workflowName = '';
  if ((workflow && schedule && schedule.eventTemplate.id === workflow.eventTemplateId)
    || (task && schedule && task.eventTemplateId === schedule.eventTemplate.id)) {
    workflowName = schedule.eventTemplate.name;
  } else if (workflow && schedule && schedule.eventTemplate.children) {
    workflowName = (schedule.eventTemplate.children.find((et) => et.id === workflow.eventTemplateId) || { name: '' }).name;
  } else if (task && schedule && schedule.eventTemplate.children.find((et) => et.id === task.eventTemplateId)) {
    workflowName = schedule.eventTemplate.children.find((et) => et.id === task.eventTemplateId).name;
  } else if (task) {
    workflowName = task.name;
  }
  return (!schedule || workflowName === schedule.name)
    ? workflowName
    : `${workflowName} (${schedule.name})`;
};

export const getBarnHorse = (task, workflow, barnHorsesMap, horseTagTypeId) => {
  let horseTag = 0;
  if (task && task.tags) {
    horseTag = task.tags.find((t) => t.tagType.id === horseTagTypeId);
  } else if (workflow && workflow.tagIds.length > 0) {
    // should be just one tag, if a horse it will match
    horseTag = { id: workflow.tagIds[0] };
  }
  return (horseTag && barnHorsesMap[horseTag.id] && barnHorsesMap[horseTag.id][0]) || null;
};

export const getTaskStatusText = (task, workflow, event, priorDayTask) => {
  if (!task && workflow && event) {
    // No task created
    if (workflow.createEvents) {
      return `Starting @ ${formatDateString(event.start)}`;
    }
    return 'Not actionable - view only';
  }
  if (task && task.archived) {
    // Created task and complete
    return 'Deleted';
  }
  if (task && task.completeDate) {
    // Created task and complete
    return 'Completed';
  }
  if (task && (!task.dueDate || new Date(task.dueDate).getTime() > new Date().getTime())) {
    // Created task with mapped schedule, not passed due
    return priorDayTask
      ? `Started ${formatDateString(task.startDate)}`
      : '';
  }
  if (task && task.dueDate && new Date(task.dueDate).getTime() < new Date().getTime()) {
    // Created task with mapped schedule\
    return `Past due since ${formatDateString(new Date(task.dueDate))}`;
  }
  return '';
};

export const getAssignment = (task, workflow) => {
  if (task && task.currentEventStatus) {
    return task.currentEventStatus.assignment
      ? (task.currentEventStatus.assignment.split(/(?=@\[)/)
        .map((user) => (
          <User
            key={user.auth0UserId}
            chip
            user={{
              auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
              userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
            }}
          />
        )))
      : (
        'Unassigned'
      );
  } if (workflow) {
    return workflow.startingAssignment
      ? (workflow.startingAssignment.split(/(?=@\[)/)
        .map((user) => (
          <User
            key={user.auth0UserId}
            chip
            user={{
              auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
              userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
            }}
            disableGutters
          />
        )))
      : (
        'Unassigned'
      );
  }
  return '';
};

const ScheduleTaskItemComponent = ({
  schedule,
  event,
  task,
  workflow,
  prominentComplete,
  hideAvatar,
  hideActionMenu,
  hideAssignment,
  noWrapText,
  showDate,
}) => {
  const horseRef = React.useRef(null);
  const dispatch = useDispatch();
  const isActionable = !!task;
  const isTaskScheduled = (!task && workflow && workflow.createEvents);
  const priorDayTask = task && new Date(task.startDate) < moment().startOf('day').toDate().getTime();
  const assignment = getAssignment(task, workflow);

  const {
    selectedBarn,
    barnHorsesMap,
  } = useSelector((state) => state.barn);
  const horseTagTypeId = (selectedBarn.barnMetadata && selectedBarn.barnMetadata.horseTagTypeId) || 0;
  const barnHorse = getBarnHorse(task, workflow, barnHorsesMap, horseTagTypeId);

  const handleOpenEventDetailsPage
    = isActionable
      ? () => { dispatch(setGlobalModals(TASK_MODAL_TYPE, task, null, null, {}, null)); }
      : (e) => { if (e && e.preventDefault) { e.preventDefault(); } };

  return (
    <Tooltip
      title={
        isActionable
          ? 'Click for more details'
          : ''
      }
    >
      <Box>
        <ListItem
          alignItems="center"
          sx={{
            cursor:
              hideActionMenu && !!task
                ? 'pointer'
                : 'default',
          }}
          divider
          onClick={handleOpenEventDetailsPage}
          dense
          button={isActionable}
        >
          {
            !hideAvatar
              && (
                <ListItemAvatar sx={{ minWidth: 36 }} ref={horseRef}>
                  {
                    barnHorse
                      ? (
                        <Horse
                          horse={barnHorse.horse}
                          hideName
                          size={30}
                          clickable
                          onClick={(e) => { dispatch(setSelectedBarnHorse(barnHorse)); e.preventDefault(); e.stopPropagation(); }}
                        />
                      )
                      : (
                        <Avatar
                          aria-label="recipe"
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: '10pt',
                          }}
                        >
                          <LocationOn />
                        </Avatar>
                      )
                  }
                </ListItemAvatar>
              )
          }
          <ListItemText
            primaryTypographyProps={{ component: 'div' }}
            secondaryTypographyProps={{ component: 'div' }}
            primary={(
              <Typography variant="body1" sx={{ lineHeight: 1 }} noWrap={noWrapText}>
                {getTaskName(task, workflow, schedule)}
              </Typography>
            )}
            secondary={(
              <>
                <Box
                  style={{
                    display: 'flex',
                    marginTop: '0.25rem',
                  }}
                >
                  <TaskStateLabel
                    archived={task && !!task.archived}
                    completed={task && !!task.completeDate}
                    // eslint-disable-next-line no-nested-ternary
                    dueDate={task
                      ? task.dueDate
                      : (workflow.dueDateTime
                        ? new Date(event.start.getTime() + workflow.dueDateTime)
                        : null)}
                    priorDayTask={priorDayTask}
                    createdDate={task && task.startDate}
                    isTaskScheduled={isTaskScheduled}
                    scheduledTime={!task && event && event.start}
                    isTaskCreated={!!task}
                  />
                  <Typography
                    sx={{ display: 'inline' }}
                    component="span"
                    variant="caption"
                    color="text.primary"
                    noWrap={noWrapText}
                  >
                    {
                      getTaskStatusText(task, workflow, event, priorDayTask)
                    }
                  </Typography>
                </Box>
                {
                  showDate && (
                    <Box
                      style={{
                        display: 'flex',
                        marginTop: '0.25rem',
                      }}
                    >
                      <Tooltip title="Task Assignment">
                        <Icon
                          style={{
                            fontSize: 18,
                            marginRight: 4,
                            color: brandColors.primary.main,
                          }}
                        >
                          calendar_month
                        </Icon>
                      </Tooltip>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.primary"
                        noWrap={noWrapText}
                      >
                        { moment((task && (task.completeDate || task.startDate)) || (event && event.start) || new Date()).format('ll') }
                      </Typography>
                    </Box>
                  )
                }
                {
                  !hideAssignment && (
                    <Box
                      style={{
                        display: 'flex',
                        marginTop: '0.25rem',
                      }}
                    >
                      <Tooltip title="Task Assignment">
                        <Icon
                          style={{
                            fontSize: 18,
                            marginRight: 4,
                            marginTop: assignment !== 'Unassigned'
                              ? 4
                              : 0,
                            color: brandColors.grey.main,
                          }}
                        >
                          assignment_ind
                        </Icon>
                      </Tooltip>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.primary"
                        noWrap={noWrapText}
                      >
                        { assignment }
                      </Typography>
                    </Box>
                  )
                }
              </>
            )}
          />
          <ListItemSecondaryAction sx={{ right: 6 }}>
            {
              !hideActionMenu
                && !!task
                && (
                  <TaskActions
                    task={task}
                    openEventDetailsPage={() => {
                      dispatch(setGlobalModals(TASK_MODAL_TYPE, task, null, null, {}, null));
                    }}
                    openHistoryDialog={() => { dispatch(setGlobalModals(STATUS_CHANGE_MODAL_TYPE, task, null, null, {}, null)); }}
                    prominentComplete={prominentComplete}
                  />
                )
            }
          </ListItemSecondaryAction>
        </ListItem>
      </Box>
    </Tooltip>
  );
};

ScheduleTaskItemComponent.propTypes = {
  schedule: PropTypes.object,
  event: PropTypes.object.isRequired,
  task: PropTypes.object,
  workflow: PropTypes.object,
  prominentComplete: PropTypes.bool,
  hideAvatar: PropTypes.bool,
  hideActionMenu: PropTypes.bool,
  hideAssignment: PropTypes.bool,
  noWrapText: PropTypes.bool,
  showDate: PropTypes.bool,
};

ScheduleTaskItemComponent.defaultProps = {
  schedule: null,
  task: null,
  workflow: null,
  prominentComplete: false,
  hideAvatar: false,
  hideActionMenu: false,
  hideAssignment: true,
  noWrapText: false,
  showDate: false,
};

export default (ScheduleTaskItemComponent);
